import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { paths } from '../utils/paths';
import axiosInstance from '../utils/api';

export const useLocales = ({enabled = true, queryParams = {}}) => {
  return useQuery({
    queryKey: ['languages', queryParams],
    queryFn: async () => {
      const response = await axiosInstance.get(paths.locales, {
        params: queryParams
      });
      return response.data;
    },
    enabled: enabled,
  });
};

export const useActiveLocales = () => {
  return useQuery({
    queryKey: ['active-locales'],
    queryFn: async () => {
      const response = await axiosInstance.get(paths.activeLocales);
      return response.data;
    },
  });
};

export const useAddLocale = ({onSuccess, onError}) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data) => {
      const response = await axiosInstance.post(paths.localesCreate, data);
      return response.data;
    },
    onSuccess: (data) => {
      // Invalidate and refetch the languages query
      queryClient.invalidateQueries({ queryKey: ['languages'] });
      
      // Call the custom onSuccess callback if provided
      if (onSuccess) {
        onSuccess(data);
      }
    },
    onError: onError,
  });
};

export const useUpdateLocale = ({onSuccess, onError}) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data) => {
      const response = await axiosInstance.post(paths.localesUpdate(data.id), data);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['languages'] });
      if (onSuccess) {
        onSuccess(data);
      }
    },
    onError: onError,
  });
};

export const useAvailableLocales = ({enabled}) => {
  return useQuery({
    queryKey: ['available-locales'],
    queryFn: async () => {
      const response = await axiosInstance.get(paths.availableLocales);
      return response.data;
    },
    enabled: enabled,
  });
};

export const useTranslationKeys = (queryParams = {}) => {
  return useQuery({
    queryKey: ['translation-keys', queryParams],
    queryFn: async () => {
      const response = await axiosInstance.get(paths.translationKeys, {
        params: queryParams
      });
      return response.data;
    },
  });
};

export const useKeyTypes = () => {
  return useQuery({
    queryKey: ['key-types'],
    queryFn: async () => {
      const response = await axiosInstance.get(paths.keyTypes);
      return response.data;
    },
  });
};

export const useUpdateOrCreateTranslationKey = ({onSuccess, onError}) => {
  return useMutation({
    mutationFn: async (data) => {
      const response = await axiosInstance.post(paths.updateOrCreateTranslationKey, data);
      return response.data;
    },
    onSuccess: onSuccess,
    onError: onError,
  });
};

export const useGetTranslationKeyDetails = ({id}) => {
  return useQuery({
    queryKey: ['translation-key-details', id],
    queryFn: async () => {
      const response = await axiosInstance.get(paths.translationKeyDetails(id));
      return response.data;
    },
    enabled: !!id,
  });
};

export const useCreateTranslationKey = ({onSuccess, onError}) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data) => {
      const response = await axiosInstance.post(paths.createTranslation, data);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['translation-keys'] });
      if (onSuccess) {
        onSuccess(data);
      }
    },
    onError: onError,
  });
};

export const useUpdateTranslationKey = ({onSuccess, onError}) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data) => {
      const response = await axiosInstance.post(paths.createTranslation, data);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['translation-keys'] });
      queryClient.invalidateQueries({ queryKey: ['translation-key-details'] });
      if (onSuccess) {
        onSuccess(data);
      }
    },
    onError: onError,
  });
};

export const useCreateTranslation = ({onSuccess, onError}) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data) => {
      const response = await axiosInstance.post(paths.createTranslation, data);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['translation-keys'] });
      queryClient.invalidateQueries({ queryKey: ['translation-key-details'] });
      if (onSuccess) {
        onSuccess(data);
      }
    },
    onError: onError,
  });
};

export const useUpdateTranslation = ({onSuccess, onError}) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data) => {
      const response = await axiosInstance.put(paths.updateTranslation(data.id), data);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['translation-keys'] });
      queryClient.invalidateQueries({ queryKey: ['translation-key-details'] });
      if (onSuccess) {
        onSuccess(data);
      }
    },
    onError: onError,
  });
};

export const useGetLanguageJson = (language = 'en') => {
  return useQuery({
    queryKey: ['language-json', language],
    queryFn: async () => {
      const response = await axiosInstance.get(paths.languageJson, {
        params: { language }
      });
      return response.data;
    },
    enabled: true,
  });
};

export const useSetUserLanguage = () => {
  return useMutation({
    mutationFn: async (locale_id) => {
      const response = await axiosInstance.post(paths.setUserLanguage, {locale_id: locale_id});
      return response.data;
    }
  });
};


export const useGetTranslationKeysCsvFile = (queryParams = {}, options = {}) => {
  return useQuery({
    queryKey: ['translation-keys-csv-file', queryParams],
    queryFn: async () => {
      const response = await axiosInstance.get(paths.translationKeysCsvFile,
        {
          params: queryParams
        }
      );
      return response.data;
    },
    ...options,
  });
};

// import csv file with form data
export const useImportTranslationKeysCsvFile = ({onSuccess, onError}) => {
  return useMutation({
    mutationFn: async (formData) => {
      const response = await axiosInstance.post(paths.importTranslationKeysCsvFile, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    },
    onSuccess: onSuccess,
    onError: onError,
  });
};